import React, { useState, useCallback, useEffect } from 'react';
import { Query } from 'react-apollo';
import { FormGroup, Input } from 'reactstrap';
import { SingleDatePicker } from 'react-dates';
import MaskInput from 'react-maskinput';
import moment from 'moment';

import i18n from '../../../../views/Pages/Login/i18n';
import { getBusinessPartnerMiddleQuery } from '../../../../queries/Queries';
import Loading from '../../../Loading';
import { useReadingData } from '../../hooks/useReadingData';
import { filterEstateById, filterMeterPointById, filterMeterById, getUniqueMeters } from '../../utils/estateUtils';
import {
  getLastReading,
  formatReadingDate,
  generateReadingMask,
  calculateReadingInputWidth,
} from '../../utils/readingUtils';

const Reading = ({ formContext, onChange }) => {
  const { readingState, updateReadingState } = useReadingData();
  const [focused, setFocused] = useState(false);

  // Sync reading state when customer changes in formContext
  useEffect(() => {
    if (formContext.customerMeterPoints && formContext.businessPartner) {
      // Reset reading state when customer changes, keeping the customer meter points info
      updateReadingState({
        mDMBPEstate: {
          mDMEstate: {
            mDMMeterPointList: [],
            customerMeterPoints: formContext.customerMeterPoints,
          },
        },
        mDMMeterPoint: {
          mDMMeterRegisterList: [],
        },
        meter: {},
      });
    }
  }, [formContext.businessPartner, formContext.customerMeterPoints, updateReadingState]);

  const handleChange = useCallback(
    (newState) => {
      updateReadingState(newState);
      onChange(newState);
    },
    [updateReadingState, onChange]
  );

  const handleEstateChange = useCallback(
    (estateList, estateId) => {
      const newEstate = filterEstateById(estateList, estateId);
      const newState = {
        mDMBPEstate: newEstate,
        mDMMeterPoint: { mDMMeterRegisterList: [] },
        meter: {},
      };
      handleChange(newState);
    },
    [handleChange]
  );

  const handleMeterPointChange = useCallback(
    (meterPointId) => {
      const newMeterPoint = filterMeterPointById(readingState.mDMBPEstate.mDMEstate.mDMMeterPointList, meterPointId);
      const newState = { mDMMeterPoint: newMeterPoint };
      handleChange(newState);
    },
    [readingState.mDMBPEstate, handleChange]
  );

  const handleMeterChange = useCallback(
    (meterId) => {
      const newMeter = filterMeterById(readingState.mDMMeterPoint.mDMMeterRegisterList, meterId);
      const newState = { meter: newMeter };
      handleChange(newState);
    },
    [readingState.mDMMeterPoint.mDMMeterRegisterList, handleChange]
  );

  const handleTypeChange = useCallback(
    (type) => {
      const newState = { type };
      handleChange(newState);
    },
    [handleChange]
  );

  const handleDateChange = useCallback(
    (date) => {
      const newState = { date };
      handleChange(newState);
    },
    [handleChange]
  );

  const handleReadingChange = useCallback(
    (registerId, value, lastReading) => {
      const newState = {
        [`reading${registerId}`]: value,
        [`lastReading${registerId}`]: lastReading,
      };
      handleChange(newState);
    },
    [handleChange]
  );

  if (!formContext.businessPartner || !formContext.businessPartner.length) {
    return null;
  }

  return (
    <Query
      query={getBusinessPartnerMiddleQuery}
      variables={{
        orderBy: 'name',
        where: `id = '${formContext.businessPartner}'`,
      }}
    >
      {(result) => {
        if (result.loading) return <Loading />;

        const data = result.data.businessPartner;
        const estateList = data[0].mDMBPEstateList;

        // Initialize state if needed
        const currentEstate = readingState.mDMBPEstate.id
          ? readingState.mDMBPEstate
          : estateList && estateList.length === 1
          ? estateList[0]
          : { mDMEstate: { mDMMeterPointList: [] } };

        // Filter meter points based on customerMeterPoints from formContext if available
        const availableMeterPoints =
          formContext.customerMeterPoints && currentEstate.mDMEstate
            ? currentEstate.mDMEstate.mDMMeterPointList.filter((mp) =>
                formContext.customerMeterPoints.includes(mp.name)
              )
            : currentEstate.mDMEstate.mDMMeterPointList;

        return (
          <div className='reading-modal'>
            <FormGroup>
              <label>{i18n.t('readings.address')}:&nbsp;</label>
              <Input
                type='select'
                value={currentEstate.id || ''}
                onChange={(e) => handleEstateChange(estateList, e.target.value)}
              >
                <option value=''>---</option>
                {estateList.map((estate) => (
                  <option key={estate.id} value={estate.id}>
                    {estate.mDMEstate.locationAddress.addressLine1}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <label>{i18n.t('readings.meterPoint')}:&nbsp;</label>
              <Input
                type='select'
                value={readingState.mDMMeterPoint.id || ''}
                onChange={(e) => handleMeterPointChange(e.target.value)}
              >
                <option value=''>---</option>
                {availableMeterPoints.map((meterPoint) => (
                  <option key={meterPoint.id} value={meterPoint.id}>
                    {meterPoint.name}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <label>{i18n.t('readings.meter')}:&nbsp;</label>
              <Input
                type='select'
                value={readingState.meter.id || ''}
                onChange={(e) => handleMeterChange(e.target.value)}
              >
                <option value=''>---</option>
                {getUniqueMeters(readingState.mDMMeterPoint.mDMMeterRegisterList).map((meter) => (
                  <option key={meter.id} value={meter.id}>
                    {meter.serialNumber}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <label>{i18n.t('readings.type')}:&nbsp;</label>
              <Input type='select' value={readingState.type} onChange={(e) => handleTypeChange(e.target.value)}>
                <option value='COU_C'>Customer</option>
                <option value='COU_E'>Estimated</option>
              </Input>
            </FormGroup>

            {readingState.type !== 'COU_E' &&
              readingState.mDMMeterPoint.mDMMeterRegisterList
                .filter(({ meter }) => meter.id === readingState.meter.id)
                .map((mDMMeterRegister) => {
                  const lastReading = getLastReading(
                    mDMMeterRegister.meter.ammMeterReadingList,
                    mDMMeterRegister.mDMRegister.product.id
                  );
                  const lastReadingDate = lastReading ? lastReading.readingdate : null;
                  return (
                    <FormGroup key={mDMMeterRegister.id}>
                      <div className='last-reading'>
                        <label>Last Read {formatReadingDate(lastReadingDate)}</label>
                        <p>{lastReading ? lastReading.newreadingmetervt : 'N/A'}</p>
                      </div>
                      <label>{mDMMeterRegister.mDMRegister.product.name}:&nbsp;</label>
                      <br />

                      <MaskInput
                        value={readingState[`reading${mDMMeterRegister.id}`] || ''}
                        onChange={(e) => handleReadingChange(mDMMeterRegister.id, e.target.value, lastReading)}
                        alwaysShowMask
                        maskChar={'0'}
                        mask={generateReadingMask(mDMMeterRegister.meterReadingDigitsCount)}
                        placeholder='20491'
                        className={'reading-input'}
                        style={{
                          width: calculateReadingInputWidth(mDMMeterRegister.meterReadingDigitsCount),
                          paddingRight: '5px',
                        }}
                      />
                    </FormGroup>
                  );
                })}

            <FormGroup>
              <label>{i18n.t('Readings Date')}:&nbsp;</label>
              <SingleDatePicker
                date={readingState.date}
                numberOfMonths={1}
                onDateChange={handleDateChange}
                isOutsideRange={(day) => day.isAfter(moment())}
                focused={focused}
                onFocusChange={({ focused }) => setFocused(focused)}
                id='cot-reading-date'
              />
            </FormGroup>
          </div>
        );
      }}
    </Query>
  );
};

export default Reading;
